<template>
  <div id="meals" class="container-meals">
    <section class="mt-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-center text-4xl font-bold mb-10 w-full mx-auto">
        Nos Délicieux Repas
      </h2>

      <!-- Categories Filter -->
      <div
        v-if="!pendingCategories"
        class="flex flex-wrap justify-center gap-4 mb-12"
      >
        <button
          :class="[
            'px-6 py-2 rounded-full transition-all duration-300 border',
            selectedCategory === null
              ? 'bg-(--main-color-700) text-white border-(--main-color-700)'
              : 'bg-white text-gray-700 border-gray-300 hover:border-(--main-color-800) hover:text-(--main-color-800) dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600',
          ]"
          @click="selectedCategory = null"
        >
          Tous
        </button>
        <button
          v-for="cat in categories"
          :key="cat.id"
          :class="[
            'px-6 py-2 rounded-full transition-all duration-300 border',
            selectedCategory === cat.id
              ? 'bg-(--main-color-700) text-white border-(--main-color-700)'
              : 'bg-white text-gray-700 border-gray-300 hover:border-(--main-color-800) hover:text-(--main-color-800) dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600',
          ]"
          @click="selectedCategory = cat.id"
        >
          {{ cat.name }}
        </button>
      </div>

      <!-- Meals Grid -->
      <div v-if="pendingMeals" class="flex justify-center py-20">
        <UIcon
          name="i-heroicons-arrow-path"
          class="animate-spin text-4xl text-(--main-color)"
        />
      </div>

      <div v-else-if="filteredMeals.length === 0" class="text-center py-20">
        <p class="text-xl text-gray-500">
          Aucun repas trouvé pour cette recherche.
        </p>
        <button
          class="mt-4 text-(--main-color) hover:underline"
          @click="clearFilters"
        >
          Voir tous les repas
        </button>
      </div>

      <div
        v-else
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12"
      >
        <div
          v-for="meal in displayedMeals"
          :key="meal.id"
          class="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700 flex flex-col"
        >
          <div class="relative h-56 overflow-hidden">
            <NuxtImg
              :src="getImageUrl(meal.image)"
              :alt="meal.name"
              class="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
              loading="lazy"
              decoding="async"
            />
            <div
              class="absolute top-4 right-4 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold shadow-sm"
            >
              {{ meal.price }} €
            </div>
          </div>

          <div class="p-5 flex-1 flex flex-col">
            <div class="flex justify-between items-start mb-2">
              <h3
                class="text-lg font-bold text-gray-900 dark:text-white line-clamp-1"
                :title="meal.name"
              >
                {{ meal.name }}
              </h3>
            </div>

            <div class="flex items-center gap-2 mb-4">
              <StarRating
                v-if="meal.ratings && meal.ratings.length > 0"
                :value="
                  meal.ratings.reduce((acc, r) => acc + r.value, 0) /
                  meal.ratings.length
                "
                class="small-stars"
              />
              <span class="text-xs text-gray-500"
                >({{ meal.ratings ? meal.ratings.length : 0 }} avis)</span
              >
            </div>

            <div class="mt-auto flex gap-2">
              <!-- Tags (Vegan/Halal) could go here -->
              <div class="flex gap-1 text-xs">
                <span
                  v-if="meal.is_vegan"
                  class="px-2 py-0.5 bg-green-100 text-green-800 rounded-md dark:bg-green-900 dark:text-green-200"
                  >Vegan</span
                >
                <span
                  v-if="meal.is_halal"
                  class="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md dark:bg-amber-900 dark:text-amber-200"
                  >Halal</span
                >
              </div>
            </div>

            <UButton
              block
              color="main"
              class="mt-4"
              @click="openMealDrawer(meal)"
            >
              Commander
            </UButton>
          </div>
        </div>
      </div>

      <!-- Load More Button -->
      <div v-if="hasMoreMeals" class="flex justify-center mb-20">
        <UButton
          size="xl"
          variant="outline"
          color="main"
          class="px-8"
          @click="loadMore"
        >
          Voir plus
        </UButton>
      </div>
    </section>

    <ClientOnly>
      <MealCustomizeDrawer
        v-if="selectedMeal"
        v-model="isDrawerOpen"
        :meal="selectedMeal"
      />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "#imports";
import type { Repas, Category } from "~/models/repas";
import MealCustomizeDrawer from "~/components/meal-customize-drawer.vue";
import StarRating from "~/components/star-rating.vue";

const { $api } = useNuxtApp();

// State
const selectedCategory = ref<number | null>(null);
const isDrawerOpen = ref(false);
const selectedMeal = ref<Repas | null>(null);
const visibleLimit = ref(4);

// Data Fetching
const { data: categories, pending: pendingCategories } = await useAsyncData<
  Category[]
>("categories", () => $api.get("/repas-category"));

const { data: meals, pending: pendingMeals, refresh: refreshMeals } = await useAsyncData<Repas[]>(
  "meals",
  () => $api.get("/repas"),
);

onMounted(async () => {
  if (!meals.value || meals.value.length === 0) {
    await refreshMeals();
  }
});

// Computed
const filteredMeals = computed(() => {
  if (!meals.value) return [];

  let result = meals.value;

  // Filter by Category
  if (selectedCategory.value) {
    result = result.filter(
      (m) => m.category && m.category.id === selectedCategory.value,
    );
  }

  return result;
});

const displayedMeals = computed(() => {
  return filteredMeals.value.slice(0, visibleLimit.value);
});

const hasMoreMeals = computed(() => {
  return displayedMeals.value.length < filteredMeals.value.length;
});

// Watchers
watch(selectedCategory, () => {
  visibleLimit.value = 4; // Reset limit when filters change
});

// Methods
const openMealDrawer = (meal: Repas) => {
  selectedMeal.value = meal;
  isDrawerOpen.value = true;
};

const clearFilters = () => {
  selectedCategory.value = null;
  visibleLimit.value = 4;
};

const loadMore = () => {
  visibleLimit.value += 4;
};
</script>

<style scoped>
.small-stars :deep(svg) {
  width: 14px;
  height: 14px;
}
</style>
